import React, { useState } from "react";

import { Pagination, PrimaryButton } from "widgets";
import { useSearchTenants } from "store/backend/apiHooks";
import SearchTenants from "components/SearchTenants/SearchTenants";
import TenantsList from "components/TenantsList/TenantsList";
import { FileUploadOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Loader from "shared/Icons/Loader/Loader";
import { TENANT_PAGE_SIZE_OPTIONS, ZERO, ONE } from "constants/constants";

import styles from "./Tenants.module.scss";


const handleOnclickUploadTenantsButton = (navigate) => {
  navigate("/uploadTenants");
};

const handleOnClickCreateTenantButton = (navigate) => {
  navigate("/createTenant");
};

const Tenants = () => {
  const navigate = useNavigate();
  const [searchTenantBy, setSearchTenantBy] = useState("code");
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(ONE);
  const [limit, setLimit] = useState(TENANT_PAGE_SIZE_OPTIONS[ZERO]);
  const [hasSearched, setHasSearched] = useState(false);

  const {
    mutate: searchTenants,
    data: tenantDetails,
    isPending: isLoading,
    isError,
    error,
    reset,
  } = useSearchTenants();

  const tenants = tenantDetails?.tenants;
  const hasTenantPayload = Array.isArray(tenants);
  const totalCount = tenantDetails?.totalCount;
  const totalPages = tenantDetails?.totalPages ?? ONE;
  const currentPage = tenantDetails?.currentPage ?? page;
  const pageSize = tenantDetails?.pageSize ?? limit;
  const showPagination = hasSearched && hasTenantPayload && !isLoading && !isError;

  const executeSearch = (targetPage, targetLimit) => {
    const trimmedSearchValue = searchValue.trim();
    if (!trimmedSearchValue) {
      return;
    }

    searchTenants({
      searchBy: searchTenantBy,
      searchTerm: trimmedSearchValue,
      page: targetPage,
      limit: targetLimit,
    });
  };

  const handleSearchClick = () => {
    setPage(ONE);
    setHasSearched(true);
    executeSearch(ONE, limit);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    executeSearch(nextPage, limit);
  };

  const handlePageSizeChange = (nextLimit) => {
    setLimit(nextLimit);
    setPage(ONE);
    executeSearch(ONE, nextLimit);
  };

  const handleClear = () => {
    setSearchValue("");
    setPage(ONE);
    setHasSearched(false);
    reset();
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.page}>
        <header className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Tenants</h1>
          <div className={styles.pageActions}>
            <PrimaryButton
              onClick={() => handleOnclickUploadTenantsButton(navigate)}
            >
              <FileUploadOutlined /> Upload Tenants
            </PrimaryButton>
            <PrimaryButton
              onClick={() => handleOnClickCreateTenantButton(navigate)}
            >
              Create Tenant
            </PrimaryButton>
          </div>
        </header>
        <div className={styles.searchContainer}>
          <h2 className={styles.searchSectionTitle}>Search Tenant</h2>
          <SearchTenants
            searchTenantBy={searchTenantBy}
            setSearchTenantBy={setSearchTenantBy}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            handleOnClickSearchButton={handleSearchClick}
            isLoading={isLoading}
            handleOnClickClearButton={handleClear}
          />
        </div>
        <section
          className={styles.resultsPanel}
          aria-labelledby="tenants-search-results-heading"
        >
          <header className={styles.resultsPanelHeader}>
            <div className={styles.resultsTitleRow}>
              <h2 id="tenants-search-results-heading" className={styles.resultsHeading}>
                Search Results
              </h2>
              {isLoading ? (
                <span className={styles.resultsHeaderMeta}>Searching…</span>
              ) : hasSearched && typeof totalCount === "number" ? (
                <span className={styles.resultsCountBadge} aria-live="polite">
                  {totalCount === ONE ? "1 tenant" : `${totalCount} tenants`}
                </span>
              ) : null}
            </div>
          </header>
          <div className={styles.resultsBody}>
            {isLoading ? (
              <div className={styles.resultsStateWrap}>
                <Loader />
              </div>
            ) : isError ? (
              <div className={styles.errorMessage}>{error}</div>
            ) : !searchValue.trim() ? (
              <p className={styles.resultsEmpty}>
                Enter a search term above, then choose <strong>Search</strong> to load tenants here.
              </p>
            ) : tenants?.length > ZERO ? (
              <TenantsList tenants={tenants} />
            ) : hasSearched && tenants?.length === ZERO ? (
              <p className={styles.resultsEmpty}>
                No tenants matched your criteria. Try another code or name, or clear and search again.
              </p>
            ) : searchValue.trim() && !hasSearched ? (
              <p className={styles.resultsEmpty}>
                You have a search term ready. Click <strong>Search</strong> to view matching tenants.
              </p>
            ) : null}
          </div>
          {showPagination && (
            <footer className={styles.resultsFooter}>
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                totalItems={totalCount}
                pageSize={pageSize}
                pageSizeOptions={TENANT_PAGE_SIZE_OPTIONS}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                disabled={isLoading}
              />
            </footer>
          )}
        </section>
      </div>
    </div>
  );
};

export default Tenants;
